import { twMerge } from 'tailwind-merge';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';

type CardShellProps = {
  children: React.ReactNode;
  className?: string;
  testId?: string;
  title?: string;
  headerOptions?: React.ReactNode;
  headerClassName?: string;
  type?: 'default' | 'secondary';
  animation?: {
    enabled?: boolean;
    delay?: number;
  };
};

const CardShell = ({
  children,
  className,
  testId,
  title,
  headerOptions,
  headerClassName,
  type = 'default',
  animation = {
    enabled: true,
    delay: 0,
  },
}: CardShellProps) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '-50px',
    triggerOnce: true,
    enabled: animation.enabled,
  });

  return (
    <div
      ref={ref}
      data-testid={testId}
      className={twMerge(
        'bg-white rounded-lg border-[1px] border-[#F0F0F9] p-6',
        animation.enabled && 'transition-all duration-1000 ease-out',
        animation.enabled &&
          (isIntersecting ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'),
        className
      )}
      style={{
        ...(animation.enabled && {
          transitionDelay: isIntersecting ? `${animation.delay}ms` : '0ms',
        }),
      }}
    >
      {/* CARD HEADER*/}

      <div
        className={twMerge(
          'flex flex-col md:flex-row md:justify-between md:items-center gap-4',
          type !== 'secondary' && 'mb-6',
          headerClassName
        )}
      >
        {title && (
          <>
            {type === 'secondary' ? (
              <h5 className="text-textMedGrey mb-2">{title}</h5>
            ) : (
              <h2
                className={twMerge(
                  'text-textDarkGrey text-2xl md:text-[34px] font-[700] font-telegraf'
                )}
              >
                {title}
              </h2>
            )}

            {headerOptions}
          </>
        )}
      </div>

      {/* CARD BODY */}
      {children}
    </div>
  );
};

export default CardShell;
